import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, BarChart3, Sparkles, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { usePageTracking } from "../hooks/usePageTracking";
import { safeJsonLd } from "../lib/safeJsonLd";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Minimum sample-size threshold below which we hide percentages to avoid
// publishing noisy / misleading numbers. We display a "building dataset"
// state until we cross the threshold.
const MIN_SAMPLE_FOR_PERCENTAGES = 50;

const PLATFORM_LABEL = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
  x: "X / Twitter",
};

const CATEGORY_LABEL = {
  csam: "Child Safety",
  dangerous_orgs: "Dangerous Organizations",
  hate_speech: "Hate Speech",
  violence_incitement: "Violence & Incitement",
  bullying_harassment: "Bullying & Harassment",
  sexual_solicitation: "Sexual Solicitation",
  nudity_sexual: "Adult Nudity & Sexual Activity",
  fraud_deception: "Fraud & Deception",
  inauthentic_behavior: "Coordinated Inauthentic Behavior",
  privacy_violations: "Privacy Violations",
  restricted_goods: "Restricted Goods",
  graphic_violence: "Graphic Violence",
  self_harm: "Suicide & Self-Injury",
  hacked_compromised: "Hacked / Compromised",
  business_quality: "Business Account Quality",
  spam_generic: "Spam / Generic Standards",
  unknown: "Uncategorized",
};

const BarRow = ({ label, count, total, showPercentage }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-baseline gap-3">
        <span className="text-sm text-slate-200 truncate">{label}</span>
        <span className="text-xs text-slate-400 flex-shrink-0">
          {count} {showPercentage && total > 0 ? `· ${pct.toFixed(1)}%` : ""}
        </span>
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full bg-blue-500 rounded-full transition-all"
          style={{ width: `${Math.max(pct, 2)}%` }}
        />
      </div>
    </div>
  );
};

export default function SuspensionTrendsPage() {
  usePageTracking("Suspension Trends", { category: "data" });

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  // SEO
  useEffect(() => {
    document.title = "Social Media Suspension Trends — Aggregate Data | Social Dispute Co";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Public aggregate data from suspension cases analyzed by Social Dispute Co. Real numbers from real cases — never fabricated. Updated continuously."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://socialdisputeco.com/data/suspension-trends");
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/triage/aggregate`)
      .then((res) => setData(res.data))
      .catch(() => setError("Couldn't load data right now. Please try again in a moment."))
      .finally(() => setLoading(false));
  }, []);

  const showPercentages = data && data.total >= MIN_SAMPLE_FOR_PERCENTAGES;

  const datasetSchema = data && data.total > 0
    ? {
        "@context": "https://schema.org",
        "@type": "Dataset",
        "name": "Social Media Suspension Trends",
        "description":
          "Aggregate, anonymized data from social media suspension cases analyzed through Social Dispute Co's free Suspension Analyzer.",
        "creator": { "@type": "Organization", "name": "Social Dispute Co." },
        "license": "https://socialdisputeco.com/terms-of-service",
        "isAccessibleForFree": true,
        "variableMeasured": ["Platform", "Enforcement Category", "Severity Tier"],
      }
    : null;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {datasetSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger -- JSON-LD is the W3C-recommended pattern; content is escaped via safeJsonLd
          dangerouslySetInnerHTML={{ __html: safeJsonLd(datasetSchema) }}
        />
      )}
      <Header />

      <main className="py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-6">
          {/* Header */}
          <div className="mb-12" data-testid="trends-hero">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 text-xs font-medium mb-5">
              <BarChart3 className="w-3.5 h-3.5" />
              Public aggregate data
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              Social Media Suspension Trends
            </h1>
            <p className="mt-5 text-lg text-slate-300 max-w-3xl leading-relaxed">
              Live aggregate data from cases analyzed through our free{" "}
              <Link to="/analyze" className="text-blue-400 hover:underline">
                Suspension Analyzer
              </Link>
              . These are <strong className="text-white">real numbers from real cases</strong> — we don't publish percentages
              we can't back up with data we actually have.
            </p>
            <p className="mt-3 text-sm text-slate-500 max-w-3xl">
              All data is anonymized at the source. We never publish individual case details, emails, or
              identifiable account information. Dataset is updated in real-time as new cases are submitted.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-3 py-16 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading…
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6 text-red-300" data-testid="trends-error">
              {error}
            </div>
          ) : (
            <>
              {/* Total counter */}
              <div className="mb-10 rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 text-center" data-testid="trends-total">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
                  Total cases analyzed
                </p>
                <p className="text-5xl md:text-6xl font-bold text-blue-400">{data.total}</p>
                <p className="mt-3 text-xs text-slate-500">
                  Last updated: {new Date(data.lastUpdated).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                </p>
              </div>

              {/* Building-dataset notice */}
              {data.total > 0 && !showPercentages && (
                <div
                  className="mb-10 rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-5"
                  data-testid="trends-building-notice"
                >
                  <p className="text-sm text-yellow-200">
                    <strong>We're still building the dataset.</strong> We don't publish percentages until we
                    have at least {MIN_SAMPLE_FOR_PERCENTAGES} cases, because small-sample percentages are
                    misleading. Counts are shown below.
                  </p>
                </div>
              )}

              {data.total === 0 ? (
                <div className="rounded-xl border border-white/10 bg-white/5 p-10 text-center" data-testid="trends-empty">
                  <h2 className="text-xl font-semibold mb-2">Dataset is being built</h2>
                  <p className="text-slate-400 text-sm mb-6">
                    No cases have been analyzed yet. Be the first to use our free analyzer — every submission helps build a public, honest picture of how the platforms actually enforce.
                  </p>
                  <Link
                    to="/analyze"
                    className="inline-flex items-center gap-2 rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-600 transition"
                  >
                    <Sparkles className="w-4 h-4" />
                    Analyze My Suspension
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6 mb-10">
                  {/* By platform */}
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="trends-by-platform">
                    <h2 className="text-lg font-semibold mb-5">Cases by Platform</h2>
                    <div className="space-y-4">
                      {data.byPlatform.map((row) => (
                        <BarRow
                          key={row.key}
                          label={PLATFORM_LABEL[row.key] || row.key}
                          count={row.count}
                          total={data.total}
                          showPercentage={showPercentages}
                        />
                      ))}
                    </div>
                  </div>

                  {/* By severity */}
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="trends-by-severity">
                    <h2 className="text-lg font-semibold mb-5">Cases by Severity</h2>
                    <div className="space-y-4">
                      {data.bySeverity.map((row) => (
                        <BarRow
                          key={row.key}
                          label={row.key}
                          count={row.count}
                          total={data.total}
                          showPercentage={showPercentages}
                        />
                      ))}
                    </div>
                  </div>

                  {/* By category */}
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:col-span-2" data-testid="trends-by-category">
                    <h2 className="text-lg font-semibold mb-5">Cases by Enforcement Category</h2>
                    <div className="space-y-3">
                      {data.byCategory.map((row) => (
                        <BarRow
                          key={row.key}
                          label={CATEGORY_LABEL[row.key] || row.key.replace(/_/g, " ")}
                          count={row.count}
                          total={data.total}
                          showPercentage={showPercentages}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Methodology footer */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8" data-testid="trends-methodology">
                <h3 className="text-lg font-semibold mb-3">How we publish this data honestly</h3>
                <ul className="space-y-2 text-sm text-slate-400 list-disc list-inside leading-relaxed">
                  <li>Every data point originates from a real submission to our free Suspension Analyzer.</li>
                  <li>We don't publish percentages until we have at least {MIN_SAMPLE_FOR_PERCENTAGES} cases — small-sample percentages are misleading.</li>
                  <li>All data is anonymized at the source. We never expose individual emails, account names, or case details on this page.</li>
                  <li>We don't publish "recovery rates" because we'd have to follow every case through to its platform decision to do so honestly. We don't have that follow-up data yet, so we don't fabricate it.</li>
                  <li>Categories are derived from each platform's own published policy documentation, not from invented labels.</li>
                </ul>
              </div>

              <div className="mt-10 text-center">
                <p className="text-sm text-slate-400 mb-4">
                  Add your case to the dataset — your data stays anonymous, and you get a free policy-mapped diagnosis.
                </p>
                <Link
                  to="/analyze"
                  className="inline-flex items-center gap-2 rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-600 transition"
                >
                  <Sparkles className="w-4 h-4" />
                  Analyze My Suspension
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
